import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumbs from '../components/Breadcrumbs';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Search, BookOpen, ArrowRight } from 'lucide-react';

const Glossary = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeLetter, setActiveLetter] = useState('all');

  const glossaryTerms = [
    {
      term: "AES (Automated Export System)",
      definition: "Electronic system used for filing Electronic Export Information (EEI) to the U.S. Census Bureau and U.S. Customs and Border Protection. Required for most exports valued over $2,500 or requiring an export license.",
      category: "Documentation",
      relatedPage: "/services/export-controls"
    },
    {
      term: "Anti-Boycott Regulations",
      definition: "U.S. laws prohibiting American companies from participating in foreign boycotts not sanctioned by the U.S. government, primarily targeting the Arab League boycott of Israel.",
      category: "Sanctions",
      relatedPage: "/services/export-controls"
    },
    {
      term: "BIS (Bureau of Industry and Security)",
      definition: "U.S. Department of Commerce agency responsible for administering and enforcing the Export Administration Regulations (EAR) for dual-use items and certain commercial products.",
      category: "Agencies",
      relatedPage: "/services/export-controls"
    },
    {
      term: "CCL (Commerce Control List)",
      definition: "List within the EAR that identifies items (commodities, software, and technology) subject to export control jurisdiction of the Bureau of Industry and Security. Items are classified by ECCN.",
      category: "Classification",
      relatedPage: "/services/product-classification"
    },
    {
      term: "Certificate of Origin",
      definition: "Document certifying the country in which goods were manufactured or produced. Required for customs clearance and preferential tariff treatment under free trade agreements.",
      category: "Documentation",
      relatedPage: "/resources"
    },
    {
      term: "CJ (Commodity Jurisdiction)",
      definition: "Formal determination by the U.S. State Department or Commerce Department as to whether an item is subject to ITAR (USML) or EAR (CCL) jurisdiction.",
      category: "Classification",
      relatedPage: "/services/product-classification"
    },
    {
      term: "DDTC (Directorate of Defense Trade Controls)",
      definition: "U.S. State Department office responsible for administering and enforcing the International Traffic in Arms Regulations (ITAR) for defense articles and services.",
      category: "Agencies",
      relatedPage: "/services/export-controls"
    },
    {
      term: "Deemed Export",
      definition: "Release of controlled technology or source code to a foreign national within the United States, treated as an export requiring authorization under EAR or ITAR.",
      category: "Export Controls",
      relatedPage: "/services/export-controls"
    },
    {
      term: "De Minimis Rule",
      definition: "Exception allowing re-export of foreign-made items incorporating limited amounts of U.S.-origin controlled content without requiring U.S. authorization. Thresholds vary by destination and item classification.",
      category: "Export Controls",
      relatedPage: "/services/export-controls"
    },
    {
      term: "DSP-5 License",
      definition: "U.S. State Department license authorizing permanent export of unclassified defense articles, related technical data, or defense services controlled under ITAR.",
      category: "Licensing",
      relatedPage: "/services/export-controls"
    },
    {
      term: "Dual-Use Items",
      definition: "Products, software, and technology that have both commercial and military or proliferation applications. Controlled under EAR and classified with ECCNs.",
      category: "Classification",
      relatedPage: "/services/product-classification"
    },
    {
      term: "EAR (Export Administration Regulations)",
      definition: "U.S. Department of Commerce regulations controlling the export and re-export of commercial and dual-use items. Administered by the Bureau of Industry and Security (BIS).",
      category: "Regulations",
      relatedPage: "/services/export-controls"
    },
    {
      term: "EAR99",
      definition: "Classification for items subject to EAR but not listed on the Commerce Control List. Generally low-level items that require export authorization only to embargoed destinations or denied parties.",
      category: "Classification",
      relatedPage: "/services/product-classification"
    },
    {
      term: "ECCN (Export Control Classification Number)",
      definition: "Five-character alphanumeric designation used to identify items on the Commerce Control List (CCL). Format: Category (0-9) + Product Group (A-E) + Reason for Control + Item Number.",
      category: "Classification",
      relatedPage: "/services/product-classification"
    },
    {
      term: "EEI (Electronic Export Information)",
      definition: "Electronic export data filed through AES for shipments requiring export reporting. Replaced the paper Shipper's Export Declaration (SED).",
      category: "Documentation",
      relatedPage: "/resources"
    },
    {
      term: "Empowered Official",
      definition: "Individual authorized to sign export license applications and who has authority to ensure compliance with export control regulations. Required role under ITAR and recommended for EAR compliance programs.",
      category: "Compliance",
      relatedPage: "/services/training-audits"
    },
    {
      term: "End-Use/End-User Checks",
      definition: "Verification procedures to ensure exported items will be used for stated purposes by legitimate end-users. Critical component of export compliance screening.",
      category: "Compliance",
      relatedPage: "/services/export-controls"
    },
    {
      term: "Entity List",
      definition: "BIS list of foreign entities determined to pose risk of diverting U.S. items to weapons of mass destruction programs or other sanctioned activities. Exports to listed entities generally require licenses.",
      category: "Sanctions",
      relatedPage: "/resources"
    },
    {
      term: "FTZ (Foreign Trade Zone)",
      definition: "Designated locations within the United States considered outside U.S. customs territory for duty and tariff purposes. Allows deferral, reduction, or elimination of customs duties.",
      category: "Customs",
      relatedPage: "/services/foreign-trade-zones"
    },
    {
      term: "HTS (Harmonized Tariff Schedule)",
      definition: "U.S. system for classifying imported goods to determine applicable duty rates. Uses 10-digit codes organized by product category and material composition.",
      category: "Classification",
      relatedPage: "/services/product-classification"
    },
    {
      term: "Incoterms",
      definition: "International Commercial Terms - standardized trade terms published by ICC defining responsibilities of buyers and sellers for delivery, risk transfer, and costs in international transactions.",
      category: "Documentation",
      relatedPage: "/resources"
    },
    {
      term: "ITAR (International Traffic in Arms Regulations)",
      definition: "U.S. State Department regulations controlling the export and temporary import of defense articles and defense services listed on the United States Munitions List (USML).",
      category: "Regulations",
      relatedPage: "/services/export-controls"
    },
    {
      term: "License Exception",
      definition: "Authorization under EAR allowing export of controlled items without an individual validated license when specific conditions are met. Examples include TMP (temporary exports), LVS (limited value shipments).",
      category: "Licensing",
      relatedPage: "/services/export-controls"
    },
    {
      term: "NLR (No License Required)",
      definition: "Designation indicating an export does not require a license under current regulations. Does not exempt shipment from export reporting or other compliance requirements.",
      category: "Licensing",
      relatedPage: "/services/export-controls"
    },
    {
      term: "OFAC (Office of Foreign Assets Control)",
      definition: "U.S. Treasury Department office administering economic and trade sanctions against targeted foreign countries, entities, and individuals.",
      category: "Agencies",
      relatedPage: "/resources"
    },
    {
      term: "Red Flags",
      definition: "Warning signs or indicators suggesting potential export control violations, diversion, or suspicious end-use. Exporters must recognize and respond to red flags with enhanced due diligence.",
      category: "Compliance",
      relatedPage: "/services/training-audits"
    },
    {
      term: "Re-export",
      definition: "Export of U.S.-origin items from one foreign country to another. Requires authorization under U.S. regulations regardless of current location outside the United States.",
      category: "Export Controls",
      relatedPage: "/services/export-controls"
    },
    {
      term: "Routed Export Transaction",
      definition: "Export where the foreign buyer assumes responsibility for export compliance while goods are still in the United States. Requires special authorization and documentation.",
      category: "Documentation",
      relatedPage: "/resources"
    },
    {
      term: "Schedule B",
      definition: "Statistical classification system for U.S. exports, using 10-digit codes. Required for completing Electronic Export Information (EEI) in AES.",
      category: "Classification",
      relatedPage: "/services/product-classification"
    },
    {
      term: "Screening",
      definition: "Process of checking parties to transactions (customers, end-users, freight forwarders, banks) against restricted party lists to identify denied or sanctioned entities.",
      category: "Compliance",
      relatedPage: "/services/export-controls"
    },
    {
      term: "SDN List (Specially Designated Nationals)",
      definition: "OFAC list of individuals and entities with whom U.S. persons are prohibited from conducting business. Includes terrorists, narcotics traffickers, and sanctioned parties.",
      category: "Sanctions",
      relatedPage: "/resources"
    },
    {
      term: "TAA (Technical Assistance Agreement)",
      definition: "Agreement with DDTC authorizing export of defense services and technical data under ITAR. Required before providing technical assistance to foreign persons.",
      category: "Licensing",
      relatedPage: "/services/export-controls"
    },
    {
      term: "Technology Transfer",
      definition: "Release or disclosure of controlled technical data or technology to foreign nationals, either through export or deemed export. Requires authorization under export control regulations.",
      category: "Export Controls",
      relatedPage: "/services/export-controls"
    },
    {
      term: "USML (United States Munitions List)",
      definition: "List of defense articles, services, and related technical data controlled under ITAR. Organized into 21 categories covering military equipment and technology.",
      category: "Classification",
      relatedPage: "/services/product-classification"
    },
    {
      term: "Validated License",
      definition: "Formal authorization from BIS or DDTC required for export of controlled items when no license exception applies. Specifies authorized items, quantities, parties, and end-use.",
      category: "Licensing",
      relatedPage: "/services/export-controls"
    },
    {
      term: "Voluntary Self-Disclosure",
      definition: "Self-initiated report to enforcement authorities describing potential or actual export control violations discovered through internal compliance efforts. Can result in reduced penalties.",
      category: "Compliance",
      relatedPage: "/services/voluntary-disclosures"
    },
    {
      term: "Wassenaar Arrangement",
      definition: "Multilateral export control regime focused on conventional arms and dual-use goods and technologies. 42 participating states coordinate national export control policies.",
      category: "International",
      relatedPage: "/resources"
    }
  ];

  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  const filteredTerms = glossaryTerms.filter(item => {
    const matchesSearch = searchQuery === '' || 
      item.term.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.definition.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesLetter = activeLetter === 'all' || 
      item.term.charAt(0).toUpperCase() === activeLetter;
    
    return matchesSearch && matchesLetter;
  });

  const groupedTerms = filteredTerms.reduce((acc, term) => {
    const firstLetter = term.term.charAt(0).toUpperCase();
    if (!acc[firstLetter]) {
      acc[firstLetter] = [];
    }
    acc[firstLetter].push(term);
    return acc;
  }, {});

  return (
    <div className="glossary-page">
      <SEO 
        title="Trade Compliance Glossary | Export Control & Customs Terms"
        description="Comprehensive glossary of international trade compliance terminology, export controls, ITAR, EAR, customs classification, and global trade regulations. Essential reference for trade professionals."
        keywords="trade compliance glossary, export control terms, ITAR definitions, EAR terminology, customs terms, ECCN, HTS codes, trade acronyms"
        path="/glossary"
      />
      <Header />
      <Breadcrumbs items={[
        { label: 'Resources', path: '/resources' },
        { label: 'Glossary', path: '/glossary' }
      ]} />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl">
            <div className="inline-block h-1 w-16 bg-slate-600 mb-8"></div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Trade Compliance Glossary
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              Comprehensive guide to international trade compliance terminology, regulations, and acronyms. Your quick reference for export controls, customs classification, and global trade requirements.
            </p>
            
            {/* Search Bar */}
            <div className="relative max-w-2xl">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search terms (e.g., ECCN, ITAR, HTS)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-600 focus:border-transparent outline-none text-base"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Alphabet Filter */}
      <section className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12 py-4">
          <div className="flex flex-wrap gap-2 items-center">
            <button
              onClick={() => setActiveLetter('all')}
              className={`px-3 py-1.5 rounded font-semibold text-sm transition-colors ${
                activeLetter === 'all'
                  ? 'bg-slate-700 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            {alphabet.map(letter => (
              <button
                key={letter}
                onClick={() => setActiveLetter(letter)}
                className={`px-3 py-1.5 rounded font-semibold text-sm transition-colors ${
                  activeLetter === letter
                    ? 'bg-slate-700 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {letter}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Terms Section */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          {filteredTerms.length === 0 ? (
            <div className="text-center py-20">
              <BookOpen className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-xl text-gray-600">No terms found matching your search.</p>
            </div>
          ) : (
            <div className="space-y-12">
              {Object.keys(groupedTerms).sort().map(letter => (
                <div key={letter} id={`letter-${letter}`}>
                  <h2 className="text-4xl font-bold text-slate-700 mb-8 pb-3 border-b-2 border-slate-200">
                    {letter}
                  </h2>
                  <div className="space-y-8">
                    {groupedTerms[letter].map((item, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-6 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between gap-4 mb-3">
                          <h3 className="text-2xl font-bold text-gray-900">{item.term}</h3>
                          <span className="text-xs font-semibold text-slate-600 bg-slate-100 px-3 py-1 rounded-full whitespace-nowrap">
                            {item.category}
                          </span>
                        </div>
                        <p className="text-base text-gray-700 leading-relaxed mb-4">
                          {item.definition}
                        </p>
                        {item.relatedPage && (
                          <Link to={item.relatedPage} className="inline-flex items-center text-slate-700 hover:text-slate-900 font-semibold text-sm">
                            Learn more <ArrowRight className="ml-1 h-4 w-4" />
                          </Link>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Stats */}
          <div className="mt-16 pt-12 border-t border-gray-200 text-center">
            <p className="text-gray-600">
              <span className="font-bold text-2xl text-slate-700">{glossaryTerms.length}</span> terms in glossary
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-700">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Need Expert Guidance?
          </h2>
          <p className="text-xl text-slate-100 mb-10 max-w-2xl mx-auto">
            Our trade compliance experts can help you navigate complex regulations and ensure your operations remain compliant.
          </p>
          <a href="https://calendly.com/nancy-wood-blackbird/0tg-trade-compliance-services" target="_blank" rel="noopener noreferrer">
            <Button size="lg" className="bg-white hover:bg-slate-100 text-slate-900 px-10 h-14 font-semibold">
              Schedule a Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Glossary;
